// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify";
import { styledScrollbar } from "@bg/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    facetsWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    searchFieldInput?: TextFieldProps;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                border: 0;
                padding: 0;
                border: 0;
            `,
        },
        headerProps: {
            css: css`
                background: ${getColor("white")};
                margin-bottom: 12px;

                button {
                    background: none;
                    display: flex;
                    justify-content: space-between;
                    &,
                    &:focus {
                        padding: 0 10px 0 0 !important;
                        border: 0 !important;
                    }

                    > span:nth-child(1) {
                        font-size: 15px;
                        font-weight: bold;
                        letter-spacing: 0.23px;
                        line-height: 20px;
                        text-transform: uppercase;
                    }

                    > span:nth-child(2) {
                        border: 0;
                        background: ${getColor("white")};
                        width: 24px;
                        height: 24px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        &,
                        > * {
                            position: static;
                        }

                        svg {
                            width: 18px;
                            height: 18px;
                            color: ${getColor("secondary")};
                        }
                    }
                }
            `,
            "aria-level": 2,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            padding: 2px 0 0 2px;
            display: block;
            &:not(:last-child) {
                margin-bottom: 9px;
            }
            > legend,
            > span {
                display: none;
            }
            > div {
                width: 100%;
                > label {
                    width: calc(100% - 26px);
                }
            }
        `,
    },
    seeAllLink: {
        color: "secondary",
        css: css`
            margin-top: 15px;
            &,
            span {
                color: ${getColor("secondary")} !important;
                font-size: 14px;
                letter-spacing: 0;
                line-height: 18px;
                font-weight: 400;
            }
        `,
    },
    seeLessLink: {
        color: "secondary",
        css: css`
            margin-top: 15px;
            &,
            span {
                color: ${getColor("secondary")} !important;
                font-size: 14px;
                letter-spacing: 0;
                line-height: 18px;
                font-weight: 400;
            }
        `,
    },
    facetsWrapper: {
        css: css`
            overflow-y: auto;
            overflow-x: hidden;
            max-height: 300px;
            ${styledScrollbar(5)};

            &.facets-collapsed {
                max-height: 180px;
            }
        `,
    },
    wrapper: {
        css: css`
            margin-bottom: 30px;
            padding-top: 17px;
            border-top: 2px solid ${getColor("common.ui3")};
        `,
    },
    searchFieldInput: {
        css: css`
            margin-bottom: 12px;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    customSettings: getSettingsCollection(state).customSettings as any,
});

type OwnProps = Props & ReturnType<typeof mapStateToProps>;

const ProductListFiltersAccordionSection: FC<OwnProps> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    customSettings,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [searchField, setSearchField] = React.useState("");
    let empty = true;

    // xng changes to handle the FDs changes
    showMoreLimit = customSettings?.maximumAttributeValues;
    // expandByDefault = true;
    // ////////

    const anySelected = facets.find(f => f.selected) !== undefined;

    const handleSearchFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchField(event.target.value);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <ManagedAccordionSection
                title={title}
                {...styles.accordionSection}
                initialExpanded={expandByDefault || anySelected}
            >
                {facets.length >= 10 && (
                    <TextField
                        value={searchField}
                        maxLength={50}
                        onChange={handleSearchFieldChange}
                        placeholder={`${translate("Filter")} ${title}`}
                        {...styles.searchFieldInput}
                    />
                )}
                <StyledWrapper {...styles.facetsWrapper} className="facets-expanded">
                    {facets?.map(facet => {
                        if (facet.name.toLowerCase().indexOf(searchField) === -1) {
                            return null;
                        }

                        empty = false;
                        return (
                            <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                                <Checkbox
                                    checked={facet.selected}
                                    onChange={() => {
                                        onChangeFacet(facet);
                                    }}
                                    data-test-selector={`facetFilter${facet.id}`}
                                >
                                    <StyledWrapper
                                        css={css`
                                            padding-right: 10px;
                                            display: flex;
                                            justify-content: space-between;
                                            width: 100%;

                                            > span {
                                                font-size: 15px;
                                                letter-spacing: 0;
                                                line-height: 18px;
                                            }
                                        `}
                                    >
                                        <span>{parse(facet.name)}</span>{" "}
                                        <span>{facet.count && facet.count !== -1 && `(${facet.count})`}</span>
                                    </StyledWrapper>
                                </Checkbox>
                            </CheckboxGroup>
                        );
                    })}
                    {empty && (
                        <Notify
                            messageType="warning"
                            body={siteMessage("Custom_ProductList_FacetsSearchNoResults", title, searchField)}
                        />
                    )}
                </StyledWrapper>
            </ManagedAccordionSection>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductListFiltersAccordionSection);
